(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-kayoz-pdp], .kayoz-pdp-main";
  const STATE_EVENT = "kayoz:variant-state";
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const within = (selector, context = document) => {
    const matches = $$(selector, context);
    if (context !== document && context.matches?.(selector)) matches.unshift(context);
    return matches;
  };
  const asArray = (value) => (Array.isArray(value) ? value : []);
  const toId = (value) => (value == null ? "" : String(value));
  const isFalse = (value) => value === false || value === "false" || value === "0";
  const isTrue = (value) => value === true || value === "true" || value === "1";

  function formatMoneyWithoutTrailingZeros(cents, format) {
    const amount = Number(typeof cents === "string" ? cents.replace(".", "") : cents);
    const formatString = format || window.theme?.moneyFormat || "${{amount}}";
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const token = formatString.match(placeholder)?.[1];

    if (!Number.isFinite(amount) || !token) return "";

    const precision = token.includes("no_decimals") || amount % 100 === 0 ? 0 : 2;
    let thousands = ",";
    let decimal = ".";

    if (token.includes("comma_separator")) {
      thousands = ".";
      decimal = ",";
    } else if (token.includes("apostrophe_separator")) {
      thousands = "'";
    } else if (token.includes("space_separator")) {
      thousands = " ";
      decimal = token.includes("period_and_space") ? "." : ",";
    }

    const parts = (amount / 100).toFixed(precision).split(".");
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const value = parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    return formatString.replace(placeholder, value);
  }

  function parseJSON(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn("Kayoz PDP: invalid JSON payload", error);
      return null;
    }
  }

  function variantIdFromControl(control) {
    if (!control) return "";
    if (control.matches("select")) return toId(control.value);
    if (control.matches("input[type='radio'], input[type='checkbox']")) {
      return control.checked ? toId(control.value || control.dataset.variantId) : "";
    }
    return toId(control.dataset.variantId || control.value);
  }

  function variantOptions(variant) {
    if (Array.isArray(variant?.options)) return variant.options;
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  }

  function variantPurchaseState(variant) {
    if (!variant) return "unselected";

    const explicit = variant.purchaseState || variant.purchase_state || variant.kayozState || variant.kayoz_state;
    if (explicit) return String(explicit).toLowerCase().replaceAll("_", "-");
    if (isFalse(variant.available)) return "unavailable";

    const quantity = Number(variant.inventoryQuantity ?? variant.inventory_quantity);
    const tracksInventory = Boolean(
      variant.inventoryManagement ?? variant.inventory_management ?? Number.isFinite(quantity)
    );
    const policy = variant.inventoryPolicy ?? variant.inventory_policy;

    if (tracksInventory && Number.isFinite(quantity)) {
      if (quantity > 0) return "in-stock";
      if (isTrue(variant.available) && (policy === "continue" || isTrue(variant.continueSelling))) {
        return "preorder";
      }
    }

    if (isTrue(variant.available) && policy === "continue") return "preorder";
    return isTrue(variant.available) ? "available" : "unavailable";
  }

  function variantEstimate(variant) {
    return (
      variant?.estimatedShipWindow ||
      variant?.estimated_ship_window ||
      variant?.estimated_ship_window_label ||
      ""
    );
  }

  function activeVariantControl(context, selector) {
    const controls = $$(selector, context);
    return (
      controls.find((control) => control.matches("select")) ||
      controls.find((control) => control.matches("input:checked")) ||
      controls.find((control) => control.getAttribute("aria-pressed") === "true") ||
      controls[0] ||
      null
    );
  }

  function displayPrice(target, variant, key = "price") {
    if (!target || !variant) return;
    const formattedKey = key === "price" ? "priceFormatted" : "compareAtPriceFormatted";
    const snakeKey = key === "price" ? "price_formatted" : "compare_at_price_formatted";
    const formatted = variant[formattedKey] || variant[snakeKey];

    if (formatted) {
      target.textContent = formatted;
      return;
    }

    const cents = Number(variant[key] ?? variant[key === "price" ? "price" : "compare_at_price"]);
    const moneyFormat = target.dataset.moneyFormat || window.theme?.moneyFormat;
    if (Number.isFinite(cents) && moneyFormat) {
      target.innerHTML = formatMoneyWithoutTrailingZeros(cents, moneyFormat);
    }
  }

  class KayozGallery {
    constructor(element) {
      this.element = element;
      this.stage = $('[data-gallery-stage]', element) || element;
      this.track = $('[data-gallery-track]', element) || this.stage;
      this.slides = $$('[data-gallery-slide]', element).slice(0, 10);
      this.thumbs = $$('[data-gallery-thumb]', element).slice(0, 10);
      this.thumbRail = $('[data-gallery-thumbnails], .kayoz-pdp-gallery__thumbs', element);
      this.previous = $('[data-gallery-prev]', element);
      this.next = $('[data-gallery-next]', element);
      this.status = $('[data-gallery-status]', element);
      this.openers = $$('[data-gallery-open]', element).slice(0, 10);
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains("is-active")));
      this.dragState = null;
      this.dragFrame = null;
      this.pendingDragX = 0;
      this.settleTimer = null;
      this.settleCancel = null;
      this.settleFinish = null;
      this.suppressClickUntil = 0;
      this.lightbox = null;
      this.nativeSwipe = null;
      this.nativePointer = null;
      this.nativeScrollTimer = null;
      this.nativeAlignFrame = null;
      this.nativeObserver = null;
      this.nativeResizeObserver = null;
      this.nativeLoopClones = [];
      this.nativeLoopJumping = false;
      this.nativeSwipeQuery = window.matchMedia("(max-width: 767px), (pointer: coarse)");
      this.handleNativeModeChange = (event) => this.setNativeSwipe(event.matches);
      this.handleNativeScroll = () => this.onNativeScroll();
      this.handleNativeScrollEnd = () => this.onNativeScrollEnd();

      if (!this.slides.length) return;
      this.bind();
      this.setNativeSwipe(this.nativeSwipeQuery.matches);
      this.show(this.index, false, false);
    }

    bind() {
      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener("click", () => this.show(index));
      });
      this.previous?.addEventListener("click", () => this.show(this.index - 1));
      this.next?.addEventListener("click", () => this.show(this.index + 1));
      this.element.addEventListener("click", (event) => {
        const opener = event.target.closest('[data-gallery-open]');
        if (!opener || !this.element.contains(opener)) return;
        if (performance.now() < this.suppressClickUntil) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        const slide = opener.closest('[data-gallery-slide]');
        const index = Number(slide?.dataset.galleryIndex);
        this.show(Number.isFinite(index) ? index : this.index, false);
        this.openLightbox(opener);
      }, true);

      this.element.addEventListener("keydown", (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (!thumb || !this.element.contains(thumb)) return;

        let requestedIndex;
        if (event.key === "ArrowLeft") requestedIndex = this.index - 1;
        else if (event.key === "ArrowRight") requestedIndex = this.index + 1;
        else if (event.key === "Home") requestedIndex = 0;
        else if (event.key === "End") requestedIndex = this.slides.length - 1;
        else return;

        event.preventDefault();
        this.show(requestedIndex);
        this.thumbs[this.index]?.focus({ preventScroll: true });
      });

      this.track.addEventListener("pointerdown", (event) => this.onPointerDown(event));
      this.track.addEventListener("pointermove", (event) => this.onPointerMove(event), { passive: false });
      this.track.addEventListener("pointerup", (event) => this.onPointerEnd(event));
      this.track.addEventListener("pointercancel", (event) => this.onPointerEnd(event, true));
      this.track.addEventListener("pointerleave", (event) => this.onPointerLeave(event));
      this.track.addEventListener("dragstart", (event) => event.preventDefault());
      this.track.addEventListener("scroll", this.handleNativeScroll, { passive: true });
      this.track.addEventListener("scrollend", this.handleNativeScrollEnd);
      if (typeof this.nativeSwipeQuery.addEventListener === "function") {
        this.nativeSwipeQuery.addEventListener("change", this.handleNativeModeChange);
      } else {
        this.nativeSwipeQuery.addListener?.(this.handleNativeModeChange);
      }
    }

    normalizeIndex(index) {
      const length = this.slides.length;
      return ((index % length) + length) % length;
    }

    setNativeSwipe(enabled) {
      const useNativeSwipe = Boolean(enabled);
      if (this.nativeSwipe === useNativeSwipe) return;

      this.resetDragVisuals();
      this.nativeSwipe = useNativeSwipe;
      this.element.classList.toggle("is-native-swipe", useNativeSwipe);
      this.nativeObserver?.disconnect();
      this.nativeObserver = null;
      this.nativeResizeObserver?.disconnect();
      this.nativeResizeObserver = null;
      window.clearTimeout(this.nativeScrollTimer);
      this.nativeScrollTimer = null;

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.hidden = useNativeSwipe ? false : !active;
        slide.setAttribute("aria-hidden", String(!active));
        if ("inert" in slide) slide.inert = !active;
      });

      if (useNativeSwipe) {
        this.setupNativeLoop();
        if ("IntersectionObserver" in window) {
          this.nativeObserver = new IntersectionObserver(
            (entries) => {
              if (!this.nativeSwipe) return;
              const visible = entries
                .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.55)
                .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
              const requestedIndex = Number(visible?.target?.dataset.galleryIndex);
              if (Number.isFinite(requestedIndex) && requestedIndex !== this.index) {
                this.show(requestedIndex, false, false);
              }
            },
            { root: this.track, threshold: [0.55, 0.75] }
          );
          this.slides.forEach((slide) => this.nativeObserver.observe(slide));
        }

        if ("ResizeObserver" in window) {
          this.nativeResizeObserver = new ResizeObserver(() => this.alignNativeTrack());
          this.nativeResizeObserver.observe(this.track);
        }
        this.alignNativeTrack(true);
      } else {
        this.teardownNativeLoop();
        this.track.scrollLeft = 0;
      }
    }

    setupNativeLoop() {
      this.teardownNativeLoop();
      if (this.slides.length < 2) return;

      const makeClone = (source, logicalIndex) => {
        const clone = source.cloneNode(true);
        clone.classList.remove("is-active", "is-adjacent");
        clone.hidden = false;
        clone.dataset.galleryLoopClone = "true";
        clone.dataset.galleryIndex = String(logicalIndex);
        clone.setAttribute("aria-hidden", "true");
        if ("inert" in clone) clone.inert = true;
        clone.removeAttribute("id");
        clone.querySelectorAll("[id]").forEach((node) => node.removeAttribute("id"));
        clone.querySelectorAll("a, button, input, select, textarea, [tabindex]").forEach((node) => {
          node.tabIndex = -1;
          node.setAttribute("aria-hidden", "true");
        });
        return clone;
      };

      const leadingClone = makeClone(this.slides[this.slides.length - 1], this.slides.length - 1);
      const trailingClone = makeClone(this.slides[0], 0);
      this.track.prepend(leadingClone);
      this.track.append(trailingClone);
      this.nativeLoopClones = [leadingClone, trailingClone];
    }

    teardownNativeLoop() {
      this.nativeLoopClones.forEach((clone) => clone.remove());
      this.nativeLoopClones = [];
      this.nativeLoopJumping = false;
    }

    nativePhysicalIndex(logicalIndex = this.index) {
      return this.nativeLoopClones.length ? logicalIndex + 1 : logicalIndex;
    }

    alignNativeTrack(immediate = false) {
      window.cancelAnimationFrame(this.nativeAlignFrame);
      const align = () => {
        this.nativeAlignFrame = null;
        if (!this.nativeSwipe) return;
        this.track.scrollTo({ left: this.nativePhysicalIndex() * this.track.clientWidth, behavior: "auto" });
      };
      if (immediate) align();
      this.nativeAlignFrame = window.requestAnimationFrame(align);
    }

    onNativeScroll() {
      if (!this.nativeSwipe || this.nativeLoopJumping) return;
      window.clearTimeout(this.nativeScrollTimer);
      this.nativeScrollTimer = window.setTimeout(() => this.syncNativeIndex(true), 140);
    }

    onNativeScrollEnd() {
      if (!this.nativeSwipe || this.nativeLoopJumping) return;
      window.clearTimeout(this.nativeScrollTimer);
      this.nativeScrollTimer = null;
      this.syncNativeIndex(true);
    }

    syncNativeIndex(settled = false) {
      if (!this.nativeSwipe) return;
      const width = Math.max(1, this.track.clientWidth);
      const physicalIndex = Math.round(this.track.scrollLeft / width);
      const hasLoop = this.nativeLoopClones.length > 0;
      const lastIndex = this.slides.length - 1;
      let requestedIndex = physicalIndex;

      if (hasLoop) {
        if (physicalIndex <= 0) requestedIndex = lastIndex;
        else if (physicalIndex >= this.slides.length + 1) requestedIndex = 0;
        else requestedIndex = physicalIndex - 1;
      }

      requestedIndex = Math.max(0, Math.min(lastIndex, requestedIndex));
      if (requestedIndex !== this.index) this.show(requestedIndex, false, false);

      if (settled && hasLoop && (physicalIndex <= 0 || physicalIndex >= this.slides.length + 1)) {
        this.nativeLoopJumping = true;
        const targetPhysicalIndex = physicalIndex <= 0 ? this.slides.length : 1;
        this.track.scrollTo({ left: targetPhysicalIndex * width, behavior: "auto" });
        window.requestAnimationFrame(() => {
          this.nativeLoopJumping = false;
        });
      }
    }

    onPointerDown(event) {
      if (this.slides.length < 2) return;
      if (!event.isPrimary) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.target.closest('[data-gallery-prev], [data-gallery-next]')) return;

      if (this.nativeSwipe) {
        this.nativePointer = {
          pointerId: event.pointerId,
          startScrollLeft: this.track.scrollLeft,
        };
        return;
      }

      // A second flick should never wait for the previous slide to finish settling.
      this.settleFinish?.();
      if (this.dragState) return;
      this.resetDragVisuals();
      const stageWidth = Math.max(1, this.track.getBoundingClientRect().width);
      const now = performance.now();
      this.dragState = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: now,
        lastDeltaX: 0,
        stageWidth,
        velocityX: 0,
        intent: null,
        direction: 0,
        adjacentIndex: null,
      };
    }

    onPointerMove(event) {
      if (this.nativeSwipe) return;
      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;

      const coalesced = typeof event.getCoalescedEvents === "function" ? event.getCoalescedEvents() : [];
      const point = coalesced.length ? coalesced[coalesced.length - 1] : event;
      const deltaX = point.clientX - state.startX;
      const deltaY = point.clientY - state.startY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);

      if (state.intent == null) {
        if (Math.max(absoluteX, absoluteY) < 5) return;
        if (absoluteY > absoluteX * 1.1) {
          state.intent = "vertical";
          return;
        }
        if (absoluteX <= absoluteY * 1.05 && Math.max(absoluteX, absoluteY) < 12) return;
        state.intent = "horizontal";
        this.stage.classList.add("is-dragging");
        try {
          this.track.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture is an enhancement; the gesture still works without it.
        }
      }

      if (state.intent !== "horizontal") return;
      event.preventDefault();

      const now = performance.now();
      const elapsed = Math.max(1, now - state.lastTime);
      const instantaneousVelocity = (point.clientX - state.lastX) / elapsed;
      state.velocityX = state.velocityX === 0
        ? instantaneousVelocity
        : (state.velocityX * 0.35) + (instantaneousVelocity * 0.65);
      state.lastX = point.clientX;
      state.lastTime = now;
      state.lastDeltaX = deltaX;

      this.pendingDragX = deltaX;
      if (this.dragFrame == null) {
        this.dragFrame = window.requestAnimationFrame(() => this.flushDragFrame());
      }
    }

    onPointerLeave(event) {
      if (this.nativeSwipe) return;
      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;
      if (state.pointerType === "mouse" && state.intent == null) this.dragState = null;
    }

    flushDragFrame() {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
      const state = this.dragState;
      if (!state || state.intent !== "horizontal") return;
      const deltaX = this.pendingDragX;
      const direction = deltaX < 0 ? 1 : -1;
      this.prepareAdjacentSlide(direction);
      this.applyDragTransform(deltaX, direction);
    }

    prepareAdjacentSlide(direction) {
      const state = this.dragState;
      if (!state) return;
      const adjacentIndex = this.normalizeIndex(this.index + direction);
      if (state.adjacentIndex === adjacentIndex && state.direction === direction) return;

      this.slides.forEach((slide, index) => {
        if (index === this.index) return;
        slide.classList.remove("is-adjacent");
        slide.style.removeProperty("transform");
        slide.hidden = true;
      });

      const adjacent = this.slides[adjacentIndex];
      adjacent.hidden = false;
      adjacent.classList.add("is-adjacent");
      adjacent.setAttribute("aria-hidden", "true");
      if ("inert" in adjacent) adjacent.inert = true;
      adjacent.style.transform = `translate3d(${direction * 100}%, 0, 0)`;
      state.direction = direction;
      state.adjacentIndex = adjacentIndex;
    }

    applyDragTransform(deltaX, direction) {
      const state = this.dragState;
      const active = this.slides[this.index];
      const adjacent = this.slides[state?.adjacentIndex];
      if (!state || !active || !adjacent) return;
      const width = state.stageWidth;
      active.style.transform = `translate3d(${deltaX}px, 0, 0)`;
      adjacent.style.transform = `translate3d(${deltaX + direction * width}px, 0, 0)`;
    }

    onPointerEnd(event, cancelled = false) {
      if (this.nativeSwipe) {
        const pointer = this.nativePointer;
        if (!pointer || pointer.pointerId !== event.pointerId) return;
        if (Math.abs(this.track.scrollLeft - pointer.startScrollLeft) > 6) {
          this.suppressClickUntil = performance.now() + 260;
        }
        this.nativePointer = null;
        return;
      }

      const state = this.dragState;
      if (!state || state.pointerId !== event.pointerId) return;
      if (state.intent !== "horizontal") {
        this.dragState = null;
        return;
      }

      if (this.dragFrame != null) this.flushDragFrame();
      const deltaX = state.lastDeltaX || (event.clientX - state.startX);
      const width = state.stageWidth;
      const threshold = Math.min(48, width * 0.12);
      const recentVelocity = performance.now() - state.lastTime < 110;
      const velocity = recentVelocity ? state.velocityX : 0;
      const projectedX = deltaX + (velocity * 120);
      const hasFlickVelocity = Math.abs(deltaX) > 10 && Math.abs(velocity) > 0.28;
      const shouldCommit = !cancelled && (
        Math.abs(deltaX) >= threshold ||
        hasFlickVelocity ||
        Math.abs(projectedX) >= width * 0.18
      );
      const direction = deltaX === 0 ? state.direction || 1 : deltaX < 0 ? 1 : -1;
      if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
      if (Math.abs(deltaX) > 8) this.suppressClickUntil = performance.now() + 360;
      this.settleDrag(shouldCommit, direction, velocity, deltaX);
    }

    settleDrag(commit, direction, velocity = 0, deltaX = 0) {
      const state = this.dragState;
      const active = this.slides[this.index];
      if (!state || !active) {
        this.resetDragVisuals();
        return;
      }

      this.prepareAdjacentSlide(direction);
      const adjacent = this.slides[state.adjacentIndex];
      if (!adjacent) {
        this.resetDragVisuals();
        return;
      }

      const nextIndex = state.adjacentIndex;
      const width = state.stageWidth;
      const remainingDistance = commit ? Math.max(0, width - Math.abs(deltaX)) : Math.abs(deltaX);
      const distanceRatio = Math.min(1, remainingDistance / width);
      const velocityBoost = Math.min(44, Math.abs(velocity) * 54);
      const settleDuration = Math.round(Math.max(170, Math.min(340, 150 + (distanceRatio * 210) - velocityBoost)));
      let finished = false;
      let onTransitionEnd;
      const cleanup = () => {
        if (onTransitionEnd) active.removeEventListener("transitionend", onTransitionEnd);
        window.clearTimeout(this.settleTimer);
        this.settleTimer = null;
        if (this.settleCancel === cancel) this.settleCancel = null;
        if (this.settleFinish === finish) this.settleFinish = null;
      };
      const cancel = () => {
        if (finished) return;
        finished = true;
        cleanup();
      };
      const finish = () => {
        if (finished) return;
        finished = true;
        cleanup();
        if (commit) {
          this.resetDragVisuals();
          this.show(nextIndex);
        } else {
          this.resetDragVisuals();
        }
      };

      if (reduceMotion.matches) {
        finish();
        return;
      }

      this.stage.classList.remove("is-dragging");
      this.stage.classList.add("is-settling");
      this.stage.style.setProperty("--kayoz-gallery-settle-duration", `${settleDuration}ms`);
      this.settleCancel = cancel;
      this.settleFinish = finish;
      window.requestAnimationFrame(() => {
        active.style.transform = `translate3d(${commit ? -direction * width : 0}px, 0, 0)`;
        adjacent.style.transform = `translate3d(${commit ? 0 : direction * width}px, 0, 0)`;
      });
      onTransitionEnd = (event) => {
        if (event.target === active && event.propertyName === "transform") finish();
      };
      active.addEventListener("transitionend", onTransitionEnd);
      this.settleTimer = window.setTimeout(finish, settleDuration + 100);
    }

    resetDragVisuals() {
      window.cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
      this.pendingDragX = 0;
      this.settleCancel?.();
      this.settleCancel = null;
      this.settleFinish = null;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
      this.stage?.classList.remove("is-dragging", "is-settling");
      this.stage?.style.removeProperty("--kayoz-gallery-settle-duration");
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.remove("is-adjacent");
        slide.style.removeProperty("transform");
        slide.hidden = this.nativeSwipe ? false : !active;
        slide.setAttribute("aria-hidden", String(!active));
        if ("inert" in slide) slide.inert = !active;
      });
      this.dragState = null;
    }

    destroy() {
      this.resetDragVisuals();
      window.cancelAnimationFrame(this.nativeAlignFrame);
      window.clearTimeout(this.nativeScrollTimer);
      this.nativeObserver?.disconnect();
      this.nativeResizeObserver?.disconnect();
      this.track.removeEventListener("scrollend", this.handleNativeScrollEnd);
      if (typeof this.nativeSwipeQuery?.removeEventListener === "function") {
        this.nativeSwipeQuery.removeEventListener("change", this.handleNativeModeChange);
      } else {
        this.nativeSwipeQuery?.removeListener?.(this.handleNativeModeChange);
      }
      this.teardownNativeLoop();
      this.closeLightbox();
      this.lightbox?.remove();
      this.lightbox = null;
    }

    show(requestedIndex, scrollThumb = true, syncStage = true) {
      this.resetDragVisuals();
      const length = this.slides.length;
      const previousIndex = this.index;
      this.index = this.normalizeIndex(requestedIndex);

      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle("is-active", active);
        slide.hidden = this.nativeSwipe ? false : !active;
        slide.setAttribute("aria-hidden", String(!active));
        if ("inert" in slide) slide.inert = !active;
      });
      this.thumbs.forEach((thumb, index) => {
        const active = index === this.index;
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
        thumb.setAttribute("aria-selected", String(active));
        thumb.tabIndex = active ? 0 : -1;
      });

      const activeThumb = this.thumbs[this.index];
      if (scrollThumb && activeThumb && this.thumbRail && this.thumbRail.scrollWidth > this.thumbRail.clientWidth + 2) {
        const targetLeft = activeThumb.offsetLeft - (this.thumbRail.clientWidth - activeThumb.offsetWidth) / 2;
        this.thumbRail.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: reduceMotion.matches ? "auto" : "smooth",
        });
      }

      if (this.nativeSwipe && syncStage) {
        const wrappedForward = previousIndex === length - 1 && requestedIndex >= length;
        const wrappedBackward = previousIndex === 0 && requestedIndex < 0;
        const physicalIndex = wrappedForward
          ? length + 1
          : wrappedBackward
            ? 0
            : this.nativePhysicalIndex(this.index);
        const left = physicalIndex * this.track.clientWidth;
        if (Math.abs(this.track.scrollLeft - left) > 1) {
          this.track.scrollTo({
            left,
            behavior: reduceMotion.matches ? "auto" : "smooth",
          });
        }
      }

      if (this.status) {
        const current = $('[data-gallery-current]', this.status);
        const total = $('[data-gallery-total]', this.status);
        if (current) current.textContent = String(this.index + 1);
        if (total) total.textContent = String(length);
        if (!current && !total) {
          const pattern = this.status.dataset.label || "{{ current }} / {{ total }}";
          this.status.textContent = pattern
            .replace("{{ current }}", String(this.index + 1))
            .replace("{{ total }}", String(length));
        }
      }
      if (this.previous) this.previous.disabled = length < 2;
      if (this.next) this.next.disabled = length < 2;
      if (this.lightbox) this.updateLightbox();
    }

    buildLightbox() {
      if (this.lightbox) return this.lightbox;
      const lightbox = document.createElement("div");
      lightbox.className = "kayoz-gallery-lightbox";
      lightbox.hidden = true;
      lightbox.setAttribute("role", "dialog");
      lightbox.setAttribute("aria-modal", "true");
      lightbox.setAttribute("aria-label", this.element.getAttribute("aria-label") || "");
      lightbox.setAttribute("tabindex", "-1");
      lightbox.innerHTML = `
        <div class="kayoz-gallery-lightbox__backdrop" data-lightbox-close aria-hidden="true"></div>
        <div class="kayoz-gallery-lightbox__content">
          <img class="kayoz-gallery-lightbox__image" data-lightbox-image alt="">
          <button type="button" class="kayoz-gallery-lightbox__close" data-lightbox-close><span aria-hidden="true">&times;</span></button>
          <button type="button" class="kayoz-gallery-lightbox__arrow kayoz-gallery-lightbox__arrow--previous" data-lightbox-prev><span aria-hidden="true">&lsaquo;</span></button>
          <button type="button" class="kayoz-gallery-lightbox__arrow kayoz-gallery-lightbox__arrow--next" data-lightbox-next><span aria-hidden="true">&rsaquo;</span></button>
          <p class="kayoz-gallery-lightbox__status" data-lightbox-status aria-live="polite"></p>
        </div>`;
      document.body.append(lightbox);
      this.lightbox = lightbox;

      const closeLabel = this.element.dataset.lightboxCloseLabel || this.element.dataset.closeLabel || "Close";
      const previousLabel = this.previous?.getAttribute("aria-label") || "Previous";
      const nextLabel = this.next?.getAttribute("aria-label") || "Next";
      $('[data-lightbox-close]:not(.kayoz-gallery-lightbox__backdrop)', lightbox)?.setAttribute("aria-label", closeLabel);
      $('[data-lightbox-prev]', lightbox).setAttribute("aria-label", previousLabel);
      $('[data-lightbox-next]', lightbox).setAttribute("aria-label", nextLabel);

      $$('[data-lightbox-close]', lightbox).forEach((button) => {
        button.addEventListener("click", () => this.closeLightbox());
      });
      $('[data-lightbox-prev]', lightbox).addEventListener("click", () => this.show(this.index - 1, false));
      $('[data-lightbox-next]', lightbox).addEventListener("click", () => this.show(this.index + 1, false));
      lightbox.addEventListener("keydown", (event) => this.onLightboxKeydown(event));
      return lightbox;
    }

    openLightbox(opener) {
      const lightbox = this.buildLightbox();
      this.returnFocus = opener;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      lightbox.hidden = false;
      document.body.setAttribute("data-kayoz-lightbox-open", "true");
      this.updateLightbox();
      $('[data-lightbox-close]:not(.kayoz-gallery-lightbox__backdrop)', lightbox)?.focus();
    }

    closeLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      this.lightbox.hidden = true;
      document.body.style.overflow = this.previousBodyOverflow || "";
      document.body.removeAttribute("data-kayoz-lightbox-open");
      (this.openers[this.index] || this.returnFocus)?.focus();
    }

    updateLightbox() {
      if (!this.lightbox || this.lightbox.hidden) return;
      const opener = this.openers[this.index];
      const sourceImage = $('img', this.slides[this.index]);
      const image = $('[data-lightbox-image]', this.lightbox);
      image.src = opener?.href || sourceImage?.currentSrc || sourceImage?.src || "";
      image.alt = sourceImage?.alt || "";
      $('[data-lightbox-status]', this.lightbox).textContent = `${this.index + 1} / ${this.slides.length}`;
      $('[data-lightbox-prev]', this.lightbox).hidden = this.slides.length < 2;
      $('[data-lightbox-next]', this.lightbox).hidden = this.slides.length < 2;
    }

    onLightboxKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.closeLightbox();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        this.show(this.index - 1, false);
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        this.show(this.index + 1, false);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = $$('button:not([hidden]):not([disabled])', this.lightbox).filter(
        (node) => node.tabIndex >= 0
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class KayozVariantTruth extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      if (!$('[aria-live]', this)) {
        this.setAttribute("aria-live", this.getAttribute("aria-live") || "polite");
        this.setAttribute("aria-atomic", this.getAttribute("aria-atomic") || "true");
      }
      $$('[data-ship-window]', this).forEach((node) => {
        node.dataset.defaultShipWindow = node.textContent.trim();
      });
    }

    update(variant, state) {
      this.dataset.state = state;
      this.dataset.variantId = toId(variant?.id);

      const stateNodes = $$('[data-truth-state], [data-variant-state]', this);
      stateNodes.forEach((node) => {
        const states = (node.dataset.truthState || node.dataset.variantState || "").split(/\s+/);
        node.hidden = !states.includes(state);
      });

      const estimate = variantEstimate(variant);
      const status = $('[data-variant-status]', this);
      if (status) {
        const source =
          $(`[data-status-${state}]`, this) ||
          $(`[data-status-state="${state}"]`, this) ||
          stateNodes.find((node) => !node.hidden);
        if (source && source !== status) {
          let copy = source.content?.textContent ?? source.textContent ?? "";
          if (state === "preorder") {
            const windowLabel = estimate || this.dataset.defaultPreorderWindow || "";
            copy = copy.replaceAll("[window]", windowLabel).replaceAll("{{ window }}", windowLabel);
          }
          status.textContent = copy;
        }
        else {
          const dataKey = `label${state.replace(/(^|-)([a-z])/g, (_match, _dash, letter) => letter.toUpperCase())}`;
          let copy = this.dataset[dataKey] || variant?.statusLabel || variant?.status_label || "";
          if (state === "preorder" && copy) {
            const windowLabel = estimate || this.dataset.defaultPreorderWindow || "";
            copy = copy.replaceAll("[window]", windowLabel).replaceAll("{{ window }}", windowLabel);
          }
          if (copy) status.textContent = copy;
        }
        status.dataset.state = state;

        const root = this.closest(ROOT_SELECTOR) || document;
        $$('[data-variant-status-inline]', root).forEach((inlineStatus) => {
          inlineStatus.textContent = status.textContent;
          inlineStatus.dataset.state = state;
          inlineStatus.classList.remove(
            "kayoz-pdp__size-status--in-stock",
            "kayoz-pdp__size-status--available",
            "kayoz-pdp__size-status--preorder",
            "kayoz-pdp__size-status--unavailable",
            "kayoz-pdp__size-status--unselected"
          );
          inlineStatus.classList.add(`kayoz-pdp__size-status--${state}`);
        });
      }

      $$('[data-ship-window]', this).forEach((node) => {
        if (state === "preorder" && estimate) node.textContent = estimate;
        else node.textContent = node.dataset.defaultShipWindow || node.textContent;
      });

      const restock = $('[data-restock-action]', this);
      if (restock) restock.hidden = state !== "unavailable";

      const mixedOrder = $('[data-variant-mixed-order]', this);
      if (mixedOrder) mixedOrder.hidden = state !== "preorder";

      const selectedCopy = stateNodes.find((node) => !node.hidden)?.textContent?.trim();
      if (selectedCopy) this.setAttribute("aria-label", selectedCopy.replace(/\s+/g, " "));
    }
  }

  class KayozBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      this.products = $$('[data-bundle-product]', this);
      this.submit = $('[data-bundle-submit]', this);
      this.message = $('[data-bundle-message]', this);
      this.summary = $('[data-bundle-summary]', this);
      this.form = $("form", this);
      this.pdpRoot = this.closest(ROOT_SELECTOR) || $(ROOT_SELECTOR);
      this.onPdpState = (event) => this.updateBase(event.detail);
      this.bind();
      this.updateSummary();
    }

    disconnectedCallback() {
      this.pdpRoot?.removeEventListener(STATE_EVENT, this.onPdpState);
    }

    bind() {
      this.form?.addEventListener("submit", (event) => {
        event.preventDefault();
        this.addBundle();
      });
      if (!this.form) this.submit?.addEventListener("click", () => this.addBundle());

      this.addEventListener("click", (event) => {
        const trigger = event.target.closest("[data-kayoz-quick-view]");
        if (!trigger) return;
        event.preventDefault();
        event.stopPropagation();

        const productUrl = trigger.dataset.productUrl;
        if (!productUrl) return;
        if (document.documentElement.dataset.kayozQuickViewBridge === "true") {
          document.dispatchEvent(new CustomEvent("kayoz:quick-view", { detail: { productUrl } }));
        } else {
          window.location.assign(productUrl);
        }
      });

      this.addEventListener("change", (event) => {
        const card = event.target.closest('[data-bundle-product]');
        if (!card) return;
        if (event.target.matches('[data-bundle-toggle]')) {
          const selected = event.target.checked;
          card.classList.toggle("is-selected", selected);
        }
        if (event.target.matches('[data-bundle-variant]')) this.updateCardPrice(card, event.target);
        card.removeAttribute("data-error");
        event.target.removeAttribute("aria-invalid");
        this.clearMessage();
        this.updateSummary();
      });

      this.pdpRoot?.addEventListener(STATE_EVENT, this.onPdpState);
    }

    updateBase({ variant, state } = {}) {
      const base = $('[data-bundle-product][data-bundle-base]', this);
      if (!base) return;
      base.dataset.selectedVariantId = toId(variant?.id);
      base.dataset.purchaseState = state || "unselected";
      const variants = parseJSON($('[data-bundle-base-variants]', base));
      const baseVariant = asArray(variants).find((candidate) => toId(candidate.id) === base.dataset.selectedVariantId);
      const option = $('[data-bundle-base-option]', base);
      if (option && baseVariant?.title) option.textContent = baseVariant.title;
      displayPrice($('[data-bundle-base-price]', base), { ...(baseVariant || {}), ...(variant || {}) });

      const primaryStatus = this.pdpRoot ? $('[data-variant-status-inline]', this.pdpRoot) : null;
      const baseStatus = $('[data-bundle-base-status]', base);
      if (baseStatus) {
        baseStatus.dataset.state = state || "unselected";
        if (primaryStatus?.textContent.trim()) baseStatus.textContent = primaryStatus.textContent.trim();
      }
      this.updateSummary();
    }

    isIncluded(card) {
      if (card.hasAttribute("data-bundle-base")) return true;
      const toggle = $('[data-bundle-toggle]', card);
      return toggle ? toggle.checked : card.dataset.included === "true";
    }

    selectedVariant(card) {
      if (card.hasAttribute("data-bundle-base") && card.dataset.selectedVariantId) {
        return card.dataset.selectedVariantId;
      }
      return variantIdFromControl(activeVariantControl(card, '[data-bundle-variant]'));
    }

    selectedItems() {
      return this.products.filter((card) => this.isIncluded(card)).map((card) => ({
        card,
        id: this.selectedVariant(card),
        quantity: Math.max(1, Number(card.dataset.quantity || 1)),
      }));
    }

    validate(items) {
      let valid = true;
      items.forEach(({ card, id }) => {
        const control = activeVariantControl(card, '[data-bundle-variant]');
        const option = control?.matches("select") ? control.selectedOptions[0] : control;
        const unavailable =
          !id ||
          card.dataset.purchaseState === "unavailable" ||
          option?.disabled ||
          isFalse(option?.dataset.available);

        card.toggleAttribute("data-error", unavailable);
        control?.setAttribute("aria-invalid", String(unavailable));
        if (unavailable) valid = false;
      });
      return valid;
    }

    itemUnavailable({ card, id }) {
      const control = activeVariantControl(card, '[data-bundle-variant]');
      const option = control?.matches("select") ? control.selectedOptions[0] : control;
      return (
        !id ||
        card.dataset.purchaseState === "unavailable" ||
        card.dataset.purchaseState === "unselected" ||
        option?.disabled ||
        isFalse(option?.dataset.available)
      );
    }

    tierQuantities() {
      const two = Math.max(2, Number(this.dataset.twoItemQuantity) || 2);
      return { two };
    }

    updateSummary() {
      const items = this.selectedItems();
      const quantities = this.tierQuantities();
      this.dataset.itemCount = String(items.length);
      const tier = items.length >= quantities.two ? "two" : "one";
      this.dataset.tier = tier;
      $$('[data-bundle-reward]', this).forEach((step) => {
        const unlocked = items.length >= quantities.two;
        step.classList.toggle("is-unlocked", unlocked);
      });
      if (this.summary) {
        $$('[data-bundle-tier]', this.summary).forEach((node) => {
          node.hidden = node.dataset.bundleTier !== tier;
        });
        const key = tier === "two" ? "summaryTwo" : "summaryOne";
        const copy = this.dataset[key] || "";
        this.summary.textContent = copy;
        this.summary.hidden = !copy;
      }
      if (this.submit) {
        const buttonKey = tier === "two" ? "buttonTwo" : "buttonOne";
        if (this.dataset[buttonKey]) this.submit.textContent = this.dataset[buttonKey];
        const base = items.find(({ card }) => card.hasAttribute("data-bundle-base"));
        const disabled = !base || this.itemUnavailable(base);
        this.submit.disabled = disabled;
        this.submit.setAttribute("aria-disabled", String(disabled));
      }
    }

    updateCardPrice(card, control) {
      const option = control.matches("select") ? control.selectedOptions[0] : control;
      const price = Number(option?.dataset.price);
      const target = $('[data-bundle-product-price]', card);
      if (target && option?.dataset.priceFormatted) {
        target.textContent = option.dataset.priceFormatted;
        return;
      }
      const moneyFormat = window.theme?.moneyFormat;
      if (target && Number.isFinite(price) && moneyFormat) {
        target.innerHTML = formatMoneyWithoutTrailingZeros(price, moneyFormat);
      }
    }

    clearMessage() {
      if (!this.message) return;
      this.message.textContent = "";
      this.message.removeAttribute("data-state");
    }

    showMessage(copy, state = "error") {
      if (!this.message) return;
      if (!this.message.hasAttribute("tabindex")) this.message.setAttribute("tabindex", "-1");
      this.message.textContent = copy || this.dataset[`message${state === "error" ? "Error" : "Success"}`] || "";
      this.message.dataset.state = state;
      this.message.focus({ preventScroll: true });
    }

    setBusy(busy) {
      this.toggleAttribute("aria-busy", busy);
      if (this.submit) {
        this.submit.disabled = busy;
        this.submit.classList.toggle("is-loading", busy);
      }
    }

    async addBundle() {
      if (this.hasAttribute("aria-busy")) return;
      const selected = this.selectedItems();
      if (!selected.length || !this.validate(selected)) {
        const base = selected.find(({ card }) => card.hasAttribute("data-bundle-base"));
        this.showMessage(
          base && this.itemUnavailable(base)
            ? this.dataset.messageBaseUnavailable || this.dataset.messageSelection
            : this.dataset.messageSelection
        );
        return;
      }

      this.clearMessage();
      this.setBusy(true);
      const root = window.Shopify?.routes?.root || "/";
      const url = `${root.endsWith("/") ? root : `${root}/`}cart/add.js`;
      const items = selected.map(({ id, quantity }) => ({ id: Number(id), quantity }));

      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          body: JSON.stringify({ items }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(payload.description || payload.message || this.dataset.messageError || "");
          error.payload = payload;
          throw error;
        }

        this.showMessage(this.dataset.messageSuccess, "success");
        const detail = { items, response: payload };
        if (items.length >= this.tierQuantities().two) {
          this.dispatchEvent(new CustomEvent("kayoz:bundle-added", { bubbles: true, detail }));
        }
        await this.refreshAndOpenQuickCart();
      } catch (error) {
        const unavailable = await this.refreshAvailability();
        const failed = unavailable[0];
        if (failed) {
          failed.card.toggleAttribute("data-error", true);
          const title = failed.card.dataset.productTitle || "That item";
          this.showMessage(`${title} is no longer available in the selected size.`);
        } else {
          this.showMessage(error.message || this.dataset.messageError);
        }
      } finally {
        this.setBusy(false);
        this.updateSummary();
      }
    }

    waitForNativeCartRefresh(quickCart, timeout = 2200) {
      const targets = [quickCart?.container, ...$$('[data-js-cart-count]', document)].filter(Boolean);

      return new Promise((resolve) => {
        let settled = false;
        const observers = [];
        const eventNames = ["cart:updated", "quick-cart:updated"];
        const finish = () => {
          if (settled) return;
          settled = true;
          observers.forEach((observer) => observer.disconnect());
          eventNames.forEach((eventName) => document.removeEventListener(eventName, finish));
          window.clearTimeout(timer);
          resolve();
        };

        targets.forEach((target) => {
          const observer = new MutationObserver(finish);
          observer.observe(target, { childList: true, characterData: true, subtree: true });
          observers.push(observer);
        });
        eventNames.forEach((eventName) => document.addEventListener(eventName, finish, { once: true }));
        const timer = window.setTimeout(finish, timeout);
      });
    }

    async refreshAndOpenQuickCart() {
      const registry = window.Shopify?.theme?.sections?.instances;
      const instances = Array.isArray(registry) ? registry : Object.values(registry || {});
      const quickCart = instances.find(
        (instance) =>
          instance?.type === "quick-cart" || instance?.container?.dataset?.sectionType === "quick-cart"
      );

      const refreshed = this.waitForNativeCartRefresh(quickCart);
      document.dispatchEvent(new CustomEvent("apps:product-added-to-cart"));
      await refreshed;

      if (quickCart?.openQuickCart) {
        quickCart.openQuickCart();
        return;
      }

      const cartIcon = $('[data-js-cart-icon]', document);
      if (cartIcon) cartIcon.click();
    }

    async refreshAvailability() {
      const root = window.Shopify?.routes?.root || "/";
      const cards = this.products.filter((card) => card.dataset.productHandle);
      await Promise.allSettled(
        cards.map(async (card) => {
          const handle = encodeURIComponent(card.dataset.productHandle);
          const response = await fetch(`${root}products/${handle}.js`, {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
          });
          if (!response.ok) return;
          const product = await response.json();
          const availability = new Map(
            asArray(product.variants).map((variant) => [toId(variant.id), Boolean(variant.available)])
          );

          if (card.hasAttribute("data-bundle-base")) {
            const available = availability.get(toId(card.dataset.selectedVariantId));
            if (available === false) {
              card.dataset.purchaseState = "unavailable";
              const status = $('[data-bundle-base-status]', card);
              if (status) {
                status.dataset.state = "unavailable";
                status.textContent = this.dataset.labelUnavailable || "Unavailable";
              }
            }
          }

          $$('[data-bundle-variant]', card).forEach((control) => {
            if (control.matches("select")) {
              Array.from(control.options).forEach((option) => {
                const available = availability.get(toId(option.value));
                if (available == null) return;
                option.disabled = !available;
                option.dataset.available = String(available);
              });
              return;
            }
            const available = availability.get(toId(control.value || control.dataset.variantId));
            if (available == null) return;
            control.disabled = !available;
            control.dataset.available = String(available);
          });
        })
      );
      return this.selectedItems().filter((item) => this.itemUnavailable(item));
    }
  }

  class KayozReelGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === "true") return;
      this.dataset.initialized = "true";
      this.items = $$('[data-reel-item]', this);
      this.track = $('[data-reel-track]', this);
      this.videos = this.items.map((item) => $('video', item)).filter(Boolean);
      this.currentIndex = 0;
      this.scrollFrame = null;
      this.videoReadyHandlers = new Map();
      this.motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.reducedMotion = Boolean(this.motionPreference?.matches);

      this.onClick = (event) => {
        const audioButton = event.target.closest('[data-reel-audio]');
        if (audioButton) {
          this.toggleAudio(audioButton);
          return;
        }

        const playbackButton = event.target.closest('[data-reel-playback]');
        if (playbackButton) this.togglePlayback(playbackButton);
      };
      this.onKeydown = (event) => {
        if (event.target !== this.track) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home') this.scrollToIndex(0);
        if (event.key === 'End') this.scrollToIndex(this.items.length - 1);
        if (event.key === 'ArrowLeft') this.scrollToIndex(this.currentIndex - 1);
        if (event.key === 'ArrowRight') this.scrollToIndex(this.currentIndex + 1);
      };
      this.onScroll = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateFromScroll();
        });
      };
      this.onVisibilityChange = () => {
        if (document.hidden) {
          this.pauseAll(true);
          return;
        }
        this.resumeVisible();
      };
      this.onPageShow = () => this.resumeVisible();
      this.onTouchStart = (event) => {
        const item = event.target.closest?.('[data-reel-item]');
        if (item && !this.reducedMotion) this.playItem(item, true);
      };
      this.onResize = () => this.updateFromScroll();
      this.onMotionPreferenceChange = (event) => {
        this.reducedMotion = event.matches;
        if (this.reducedMotion) this.pauseAll(true);
        else this.resumeVisible();
      };

      this.addEventListener('click', this.onClick);
      this.track?.addEventListener('keydown', this.onKeydown);
      this.track?.addEventListener('scroll', this.onScroll, { passive: true });
      this.track?.addEventListener('touchstart', this.onTouchStart, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.motionPreference?.addEventListener?.('change', this.onMotionPreferenceChange);

      this.items.forEach((item, index) => {
        item.dataset.reelIndex = String(index);
        const video = $('video', item);
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.autoplay = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.removeAttribute('controls');
        video.setAttribute('autoplay', '');
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        item.dataset.playing = String(!video.paused && !video.ended);
        video.addEventListener('play', () => this.setPlaybackState(item, true));
        video.addEventListener('pause', () => this.setPlaybackState(item, false));
        video.addEventListener('volumechange', () => this.setAudioState(item, !video.muted));
        const readyHandler = () => {
          if (item.dataset.reelVisible === 'true' && !this.reducedMotion && !document.hidden) {
            this.playItem(item);
          }
        };
        this.videoReadyHandlers.set(video, readyHandler);
        video.addEventListener('loadeddata', readyHandler);
        video.addEventListener('canplay', readyHandler);
      });
      this.observeVisibility();
      this.setCurrent(0, true);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.track?.removeEventListener('touchstart', this.onTouchStart);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('resize', this.onResize);
      this.motionPreference?.removeEventListener?.('change', this.onMotionPreferenceChange);
      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.itemObserver?.disconnect();
      this.videoReadyHandlers.forEach((handler, video) => {
        video.removeEventListener('loadeddata', handler);
        video.removeEventListener('canplay', handler);
      });
      this.videoReadyHandlers.clear();
      this.pauseAll(true);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.items.forEach((item) => {
          item.dataset.reelVisible = 'true';
          if (!this.reducedMotion) this.playItem(item);
        });
        return;
      }

      this.itemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const item = entry.target;
            const visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
            item.dataset.reelVisible = String(visible);
            if (visible && !this.reducedMotion && !document.hidden) this.playItem(item);
            else this.pauseItem(item, true);
          });
        },
        { threshold: [0, 0.2, 0.55, 0.9] }
      );
      this.items.forEach((item) => this.itemObserver.observe(item));
    }

    toggleAudio(button) {
      const item = button.closest('[data-reel-item]');
      if (!item) return;
      const video = $('video', item);
      if (!video) return;

      const enableSound = video.muted;
      if (enableSound) {
        this.items.forEach((otherItem) => {
          const otherVideo = $('video', otherItem);
          if (!otherVideo || otherVideo === video) return;
          otherVideo.muted = true;
          this.setAudioState(otherItem, false);
        });
      }
      video.muted = !enableSound;
      this.setAudioState(item, enableSound);
      // Re-issue play from the tap gesture. Some mobile browsers pause a
      // previously autoplaying video at the moment it becomes audible.
      this.playItem(item, true);
    }

    togglePlayback(button) {
      const item = button.closest('[data-reel-item]');
      const video = item && $('video', item);
      if (!video) return;
      if (video.paused) this.playItem(item, true);
      else this.pauseItem(item, false);
    }

    playItem(item, userInitiated = false) {
      const video = $('video', item);
      if (!video || (!userInitiated && (this.reducedMotion || document.hidden))) return;
      delete item.dataset.autoplayBlocked;
      const playPromise = video.play();
      if (playPromise?.then) {
        playPromise
          .then(() => {
            delete item.dataset.autoplayBlocked;
            this.setPlaybackState(item, true);
          })
          .catch((error) => {
            if (error?.name === 'AbortError') return;
            item.dataset.autoplayBlocked = 'true';
            this.setPlaybackState(item, false);
          });
      }
    }

    pauseItem(item, resetAudio = false) {
      const video = $('video', item);
      if (!video) return;
      if (!video.paused) video.pause();
      if (resetAudio) {
        video.muted = true;
        this.setAudioState(item, false);
      }
      this.setPlaybackState(item, false);
    }

    pauseAll(resetAudio = false) {
      this.items.forEach((item) => this.pauseItem(item, resetAudio));
    }

    resumeVisible() {
      if (this.reducedMotion || document.hidden) return;
      this.items.forEach((item) => {
        if (item.dataset.reelVisible === 'true') this.playItem(item);
      });
    }

    scrollToIndex(index) {
      if (!this.track || !this.items.length) return;
      const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
      const item = this.items[nextIndex];
      const trackRect = this.track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const left = this.track.scrollLeft + itemRect.left - trackRect.left;
      this.track.scrollTo({ left, behavior: this.reducedMotion ? 'auto' : 'smooth' });
      this.setCurrent(nextIndex);
    }

    updateFromScroll() {
      if (!this.track || !this.items.length) return;
      const trackLeft = this.track.getBoundingClientRect().left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      this.setCurrent(closestIndex);
    }

    setCurrent(index, force = false) {
      if (!this.items.length) return;
      const nextIndex = Math.max(0, Math.min(index, this.items.length - 1));
      if (!force && nextIndex === this.currentIndex) return;
      this.currentIndex = nextIndex;
    }

    setAudioState(item, soundOn) {
      const button = $('[data-reel-audio]', item);
      if (!button) return;
      button.setAttribute('aria-pressed', String(soundOn));
      const label = soundOn ? button.dataset.muteLabel : button.dataset.unmuteLabel;
      if (label) button.setAttribute('aria-label', label);
    }

    setPlaybackState(item, playing) {
      const button = $('[data-reel-playback]', item);
      if (button) {
        button.setAttribute('aria-pressed', String(playing));
        const label = playing ? button.dataset.pauseLabel : button.dataset.playLabel;
        if (label) button.setAttribute('aria-label', label);
      }
      item.dataset.playing = String(playing);
    }
  }

  class KayozPdpController {
    constructor(root) {
      this.root = root;
      this.variantPayload =
        parseJSON($('[data-kayoz-variant-json]', root)) ||
        parseJSON($('[data-kayoz-variants]', root)) ||
        parseJSON($('[data-variant-json]', root));
      const rawVariants = Array.isArray(this.variantPayload)
        ? this.variantPayload
        : asArray(this.variantPayload?.variants);
      this.variants = new Map(rawVariants.map((variant) => [toId(variant.id), variant]));
      this.selectedVariant = null;
      this.state = "unselected";
      this.galleryRequest = null;
      this.galleryRequestMediaId = "";
      this.galleryRequestId = 0;
      root.kayozPdp = this;
      this.bind();
      this.setInitialVariant();
    }

    bind() {
      this.onThemeVariant = (event) => {
        const incoming = event.detail?.variant;
        if (!incoming) {
          this.update(null);
          return;
        }
        const enriched = this.variants.get(toId(incoming.id));
        if (!enriched && this.variants.size) return;
        this.update({ ...incoming, ...(enriched || {}) });
      };
      document.addEventListener("product:variant-change", this.onThemeVariant);

      this.root.addEventListener("change", (event) => {
        if (event.target.closest('kayoz-final-offer, kayoz-bundle-builder')) return;
        if (!event.target.matches('[data-product-option] select, [data-variant-select], [name="id"]')) return;
        window.requestAnimationFrame(() => this.readNativeVariant());
      });
      this.root.addEventListener("click", (event) => {
        if (!event.target.closest('[data-product-option] [data-button]')) return;
        window.requestAnimationFrame(() => this.readNativeVariant());
      });
    }

    destroy() {
      document.removeEventListener("product:variant-change", this.onThemeVariant);
      if (this.galleryRequest && !this.galleryRequest.signal.aborted) this.galleryRequest.abort();
      this.galleryRequest = null;
      this.galleryRequestMediaId = "";
      if (this.root.kayozPdp === this) delete this.root.kayozPdp;
    }

    setInitialVariant() {
      const selected = $('[data-variant-select]', this.root);
      const fromControl = toId(selected?.value);
      const fromRoot = toId(this.root.dataset.selectedVariantId);
      const initial = this.variants.get(fromControl || fromRoot) || this.variants.values().next().value || null;
      this.update(initial);
    }

    readNativeVariant() {
      const select = $('[data-variant-select]', this.root);
      const variant = this.variants.get(toId(select?.value));
      if (variant) this.update(variant);
    }

    update(variant) {
      const nextVariant = variant || null;
      const nextState = variantPurchaseState(nextVariant);
      void this.syncGallery(nextVariant).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[Kayoz PDP] Unable to update color gallery.', error);
      });
      if (toId(this.selectedVariant?.id) === toId(nextVariant?.id) && this.state === nextState) return;

      this.selectedVariant = nextVariant;
      this.state = nextState;
      this.root.dataset.selectedVariantId = toId(variant?.id);
      this.root.dataset.purchaseState = this.state;

      $$('kayoz-variant-truth', this.root).forEach((truth) => truth.update?.(variant, this.state));
      $$('[data-kayoz-selected-variant]', this.root).forEach((node) => {
        node.dataset.variantId = toId(variant?.id);
      });
      $$('[data-kayoz-price-output]', this.root).forEach((node) => displayPrice(node, variant));

      const activeTruth = $('kayoz-variant-truth [data-truth-state]:not([hidden])', this.root);
      $$('[data-kayoz-sticky-status]').forEach((node) => {
        node.dataset.state = this.state;
        if (activeTruth) node.textContent = activeTruth.textContent.trim();
      });

      this.root.dispatchEvent(
        new CustomEvent(STATE_EVENT, {
          bubbles: false,
          detail: { variant, state: this.state },
        })
      );
    }

    featuredMediaId(variant) {
      return toId(
        variant?.featured_media?.id ||
          variant?.featuredMedia?.id ||
          variant?.featured_media_id ||
          variant?.featuredMediaId
      );
    }

    async syncGallery(variant) {
      const variantId = toId(variant?.id);
      const featuredMediaId = this.featuredMediaId(variant);
      const currentGallery = $('[data-kayoz-gallery]', this.root);

      if (!variantId || !featuredMediaId || !currentGallery) return;
      if (toId(currentGallery.dataset.featuredMediaId) === featuredMediaId) return;
      if (this.galleryRequest && this.galleryRequestMediaId === featuredMediaId) return;

      const handle = this.root.dataset.productHandle;
      const sectionId = this.root.dataset.sectionId;
      if (!handle || !sectionId) return;

      if (this.galleryRequest && !this.galleryRequest.signal.aborted) this.galleryRequest.abort();
      const request = new AbortController();
      const requestId = ++this.galleryRequestId;
      this.galleryRequest = request;
      this.galleryRequestMediaId = featuredMediaId;
      currentGallery.setAttribute('aria-busy', 'true');

      try {
        const productsBase = window.theme?.routes?.products || '/products';
        const url = `${productsBase}/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(
          sectionId
        )}&variant=${encodeURIComponent(variantId)}`;
        const response = await fetch(url, {
          signal: request.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Gallery request failed (${response.status})`);

        const html = await response.text();
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-kayoz-gallery]');
        if (!source) throw new Error('Gallery response did not include a gallery');
        if (requestId !== this.galleryRequestId || toId(this.selectedVariant?.id) !== variantId) return;

        const mountedGallery = $('[data-kayoz-gallery]', this.root);
        if (!mountedGallery) return;
        mountedGallery.kayozGallery?.destroy?.();
        mountedGallery.replaceWith(source);
        initializeGalleryElement(source);
      } catch (error) {
        if (error?.name !== 'AbortError' && !request.signal.aborted) {
          console.warn('[Kayoz PDP] Unable to update color gallery.', error);
        }
      } finally {
        if (requestId === this.galleryRequestId) {
          this.galleryRequest = null;
          this.galleryRequestMediaId = "";
          $('[data-kayoz-gallery]', this.root)?.removeAttribute('aria-busy');
        }
      }
    }

    selectPrimaryVariant(variant) {
      const options = variantOptions(variant);
      const productOptions = $$('.product__option[data-product-option]', this.root);
      let changed = false;

      options.forEach((value, index) => {
        const wrapper = productOptions[index];
        if (!wrapper) return;
        const button = $$('[data-option-value]', wrapper).find(
          (candidate) => candidate.dataset.optionValue === String(value)
        );
        if (button && !button.classList.contains("selected")) {
          button.click();
          changed = true;
          return;
        }
        const select = $("select", wrapper);
        if (select && select.value !== String(value)) {
          select.value = String(value);
          select.dispatchEvent(new Event("change", { bubbles: true }));
          changed = true;
        }
      });

      const nativeSelect = $('[data-variant-select]', this.root);
      if (nativeSelect) nativeSelect.value = toId(variant.id);
      if (!changed) this.update(variant);
      window.requestAnimationFrame(() => this.update(this.variants.get(toId(variant.id)) || variant));
    }
  }

  function register(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  function initializeStickyScrollCta(context = document) {
    within('[data-kayoz-mobile-sticky]', context).forEach((bar) => {
      if (bar.dataset.kayozScrollCtaInitialized === "true") return;
      const sectionId = bar.dataset.sectionId;
      const root = sectionId
        ? $(`[data-kayoz-pdp][data-section-id="${CSS.escape(sectionId)}"], .kayoz-pdp-main[data-section-id="${CSS.escape(sectionId)}"]`)
        : $(ROOT_SELECTOR);
      const target = root?.querySelector('.kayoz-pdp__size-controls') || root?.querySelector('[data-primary-product-form]');
      const primaryCta = root?.querySelector('[data-primary-product-form] [data-add-to-cart]');
      const button = $('[data-kayoz-sticky-scroll]', bar);
      if (!root || !button || !target || !primaryCta) return;

      bar.dataset.kayozScrollCtaInitialized = "true";
      bar.hidden = false;
      bar.setAttribute('aria-hidden', 'true');
      const mobile = window.matchMedia('(max-width: 767px)');
      const listeners = [];
      let frame = null;
      let focusTimer = null;

      const setVisible = (visible) => {
        const next = Boolean(visible);
        if (bar.dataset.kayozStickyVisible !== String(next)) {
          bar.dataset.kayozStickyVisible = String(next);
          bar.setAttribute('aria-hidden', String(!next));
          if ('inert' in bar) bar.inert = !next;
        }
        document.documentElement.style.setProperty('--sticky-atc-bar-height', next ? `${bar.offsetHeight}px` : '0px');
      };

      const update = () => {
        frame = null;
        if (!mobile.matches || !document.documentElement.contains(root)) {
          setVisible(false);
          return;
        }

        const primaryRect = primaryCta.getBoundingClientRect();
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const primaryHasPassed = primaryRect.bottom <= 0;
        const finalOffer = $('#kayoz-bundle-builder');
        let finalOfferVisible = false;
        if (finalOffer) {
          const finalRect = finalOffer.getBoundingClientRect();
          finalOfferVisible = finalRect.top < viewportHeight && finalRect.bottom > 0;
        }
        setVisible(primaryHasPassed && !finalOfferVisible);
      };

      const scheduleUpdate = () => {
        if (frame != null) return;
        frame = window.requestAnimationFrame(update);
      };

      const listen = (targetNode, eventName, handler, options) => {
        targetNode?.addEventListener?.(eventName, handler, options);
        listeners.push(() => targetNode?.removeEventListener?.(eventName, handler, options));
      };

      const onButtonClick = (event) => {
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion.matches ? "auto" : "smooth", block: "center" });
        const focusTarget = target.querySelector(
          'button:not([disabled]), [role="radio"][tabindex="0"], select:not([disabled]), input:not([disabled])'
        );
        window.clearTimeout(focusTimer);
        focusTimer = window.setTimeout(
          () => focusTarget?.focus({ preventScroll: true }),
          reduceMotion.matches ? 0 : 360
        );
      };

      listen(button, 'click', onButtonClick);
      listen(window, 'scroll', scheduleUpdate, { passive: true });
      listen(window, 'resize', scheduleUpdate, { passive: true });
      listen(window, 'pageshow', scheduleUpdate);
      listen(window.visualViewport, 'resize', scheduleUpdate, { passive: true });
      listen(window.visualViewport, 'scroll', scheduleUpdate, { passive: true });
      listen(mobile, 'change', scheduleUpdate);

      bar.kayozStickyController = {
        destroy() {
          listeners.forEach((remove) => remove());
          window.cancelAnimationFrame(frame);
          window.clearTimeout(focusTimer);
          setVisible(false);
        },
      };
      scheduleUpdate();
    });
  }

  function initializeSizeChartPlacement(context = document) {
    $$(ROOT_SELECTOR, context).forEach((root) => {
      const host = $('.kayoz-pdp__size-app', root);
      if (!host || host.dataset.kayozSizeChartInitialized === 'true') return;
      host.dataset.kayozSizeChartInitialized = 'true';

      const placeChart = () => {
        const chart = $('.ks-chart-container.sizing-chart-container', root);
        if (!chart) return;
        if (chart.parentElement !== host) host.appendChild(chart);

        const customLabel = host.closest('[data-size-chart-label]')?.dataset.sizeChartLabel?.trim();
        if (!customLabel) return;
        const control = chart.matches('a, button') ? chart : $('a, button', chart) || chart;
        const walker = document.createTreeWalker(control, NodeFilter.SHOW_TEXT);
        const textNodes = [];
        let textNode = walker.nextNode();
        while (textNode) {
          if (textNode.nodeValue.trim()) textNodes.push(textNode);
          textNode = walker.nextNode();
        }
        const visibleLabel = textNodes.at(-1);
        if (visibleLabel && visibleLabel.nodeValue.trim() !== customLabel) visibleLabel.nodeValue = ` ${customLabel}`;
        control.setAttribute('aria-label', customLabel);
      };

      placeChart();
      const observer = new MutationObserver(placeChart);
      observer.observe(root, { childList: true, subtree: true });
      host.kayozSizeChartObserver = observer;
    });
  }

  function initializePdpHeader() {
    if (!$(ROOT_SELECTOR)) return;
    document.body.classList.add('kayoz-pdp-header-transparent', 'header-transparent');
    const header = $('header.header');
    if (!header) return;
    header.classList.add('header--transparent');
    header.setAttribute('data-transparent-header', 'true');
    header.dataset.kayozTransparentHeader = 'true';
  }

  function initializeProductDetailsDisclosure(context = document) {
    within('[data-product-details]', context).forEach((details) => {
      if (details.dataset.productDetailsInitialized === 'true') return;
      const content = $('[data-product-details-content]', details);
      const button = $('[data-product-details-toggle]', details);
      if (!content || !button) return;

      details.dataset.productDetailsInitialized = 'true';
      details.dataset.detailsReady = 'true';
      const update = (expanded) => {
        details.dataset.detailsExpanded = String(expanded);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded
          ? details.dataset.labelClose || 'Read less'
          : details.dataset.labelOpen || 'Read more';
      };

      update(false);
      button.hidden = false;
      window.requestAnimationFrame(() => {
        if (content.scrollHeight <= content.clientHeight + 2) {
          details.removeAttribute('data-details-ready');
          button.hidden = true;
        }
      });
      button.addEventListener('click', () => update(details.dataset.detailsExpanded !== 'true'));
    });
  }

  function initializeFitDisclosure(context = document) {
    within('[data-fit-disclosure]', context).forEach((disclosure) => {
      if (disclosure.dataset.fitDisclosureInitialized === 'true') return;
      const button = $('[data-fit-toggle]', disclosure);
      const secondaryMeters = $$('[data-fit-secondary]', disclosure);
      if (!button || secondaryMeters.length === 0) return;

      disclosure.dataset.fitDisclosureInitialized = 'true';
      const update = (expanded) => {
        disclosure.dataset.fitExpanded = String(expanded);
        button.setAttribute('aria-expanded', String(expanded));
        button.textContent = expanded
          ? disclosure.dataset.labelClose || 'See less'
          : disclosure.dataset.labelOpen || 'See more';
        secondaryMeters.forEach((meter) => {
          meter.hidden = !expanded;
        });
      };

      button.hidden = false;
      update(false);
      button.addEventListener('click', () => update(disclosure.dataset.fitExpanded !== 'true'));
    });
  }

  function initializeGalleryElement(gallery) {
    if (!gallery || gallery.dataset.galleryInitialized === "true") return;
    gallery.dataset.galleryInitialized = "true";
    gallery.kayozGallery = new KayozGallery(gallery);
  }

  function initialize(context = document) {
    register("kayoz-variant-truth", KayozVariantTruth);
    register("kayoz-bundle-builder", KayozBundleBuilder);
    register("kayoz-reel-gallery", KayozReelGallery);

    within('[data-kayoz-gallery]', context).forEach((gallery) => {
      initializeGalleryElement(gallery);
    });

    within(ROOT_SELECTOR, context).forEach((root) => {
      if (root.dataset.pdpInitialized === "true") return;
      root.dataset.pdpInitialized = "true";
      root.kayozPdpController = new KayozPdpController(root);
    });

    initializeStickyScrollCta(context);
    initializeSizeChartPlacement(context);
    initializeProductDetailsDisclosure(context);
    initializeFitDisclosure(context);
    initializePdpHeader();
  }

  function destroy(context) {
    within('[data-kayoz-gallery]', context).forEach((gallery) => {
      gallery.kayozGallery?.destroy?.();
      delete gallery.kayozGallery;
    });
    within(ROOT_SELECTOR, context).forEach((root) => {
      root.kayozPdpController?.destroy?.();
      delete root.kayozPdpController;
    });
    within('[data-kayoz-mobile-sticky]', context).forEach((bar) => {
      bar.kayozStickyController?.destroy?.();
      delete bar.kayozStickyController;
    });
    within('.kayoz-pdp__size-app', context).forEach((host) => {
      host.kayozSizeChartObserver?.disconnect?.();
      delete host.kayozSizeChartObserver;
    });
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  else initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    destroy(event.target);
  });
})();
